<script setup lang="ts">
import { computed, ref } from 'vue';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import OverlayBadge from 'primevue/overlaybadge';
import type { MenuItem } from 'primevue/menuitem';
import type { DashboardEmail, EmailAttachment } from '@apparatix/schemas/DashboardEmails';
import { useToastNotifications, useUtilities } from '@apparatix/composables';
import { Noir } from '@apparatix/theme/Noir';

type Props = {
  email: DashboardEmail,
};

const props = defineProps<Props>();

const { arrayHasValues, downloadFile } = useUtilities();
const { showError } = useToastNotifications();

const menuRef = ref();
const isDownloading = ref(false);

const attachments = computed(() => props.email.attachments ?? []);

const getAttachmentFilename = (attachment: EmailAttachment): string => {
  const filename = attachment.filename?.trim();

  return filename || `attachment-${attachment.id}`;
};

const getAttachmentLabel = (attachment: EmailAttachment): string => getAttachmentFilename(attachment);

const getXsrfToken = (): string | undefined => {
  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('XSRF-TOKEN='));

  if (!cookie) {
    return undefined;
  }

  return decodeURIComponent(cookie.split('=').slice(1).join('='));
};

const downloadAttachment = async (attachment: EmailAttachment): Promise<void> => {
  isDownloading.value = true;

  try {
    await downloadFile(
      attachment.downloadUrl ?? `/dashboard/emails/${props.email.id}/attachments/${attachment.id}/download`,
      getAttachmentFilename(attachment),
      attachment.contentType ?? 'application/octet-stream',
      true,
    );
  } catch {
    showError(`Unable to download ${getAttachmentFilename(attachment)}. Please try again, or contact support`, 'Error');
  } finally {
    isDownloading.value = false;
  }
};

const downloadAllAttachments = async (): Promise<void> => {
  isDownloading.value = true;

  try {
    const response = await fetch(`/dashboard/emails/${props.email.id}/attachments/download`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/zip, application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-XSRF-TOKEN': getXsrfToken(),
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      throw new Error('Download request failed.');
    }

    await downloadFile(await response.blob(), `email-${props.email.id}-attachments.zip`, 'application/zip');
  } catch {
    showError('Unable to download attachments. Please try again, or contact support', 'Error');
  } finally {
    isDownloading.value = false;
  }
};

const menuItems = computed<MenuItem[]>(() => {
  if (!arrayHasValues(attachments.value)) {
    return [];
  }

  return [
    {
      label: 'Download all',
      icon: 'pi pi-download',
      command: downloadAllAttachments,
      disabled: isDownloading.value,
    },
    { separator: true },
    ...attachments.value.map(attachment => ({
      label: getAttachmentLabel(attachment),
      icon: 'pi pi-paperclip',
      command: () => downloadAttachment(attachment),
      disabled: isDownloading.value,
    })),
  ];
});

const onClick = (event: MouseEvent): void => {
  menuRef.value.toggle(event);
};
</script>

<template>
  <OverlayBadge v-if="arrayHasValues(attachments)" :value="attachments.length" :dt="Noir.badge">
    <Button
      icon="pi pi-paperclip"
      severity="secondary"
      size="small"
      :loading="isDownloading"
      :dt="Noir.button"
      rounded
      aria-label="Download email attachments"
      @click="onClick"
    />
  </OverlayBadge>
  <Menu ref="menuRef" :model="menuItems" popup />
</template>
